<template>
  <pf-radio
    v-if="nullLabel"
    :label="nullLabel ?? ''"
    :name="name"
    :required="required"
    :value="undefined"
    :checked="modelValue === '' || modelValue === undefined"
    @change="emit('update:modelValue', undefined as MaybeRequired<T | undefined, R>)"
  />
  <pf-grid style="max-width: 80ch">
    <pf-grid-item :sm="gridSpan(4)" :lg="gridSpan(3)" v-for="o in normalizedOptions">
      <pf-radio
        v-if="o"
        :key="o.value"
        :label="o.label"
        :name="name"
        :required="booleanish(required)"
        :value="o.value"
        :checked="o.value === modelValue"
        @change="emit('update:modelValue', o.value as MaybeRequired<T | undefined, R>)"
      />
    </pf-grid-item>
  </pf-grid>
</template>

<script lang="ts">
import type { GridSpan } from '@vue-patternfly/core/layouts/Grid/Grid.vue';

interface WizardQuestionOptionsProps<O extends readonly string[] | Record<string, string>, N extends string | undefined, T = ExtractValueType<O>> {
  modelValue: T | undefined;
  readonly options: O;
  name: string;
  required?: boolean;
  nullLabel?: N;
  isPopup?: boolean;
}

export type ExtractValueType<O> = O extends readonly (infer U)[] ? U : O extends Record<infer K, string> ? K : never;
</script>

<script setup lang="ts" generic="O extends readonly string[] | Record<string, string>, N extends string | undefined = undefined">
import type { MaybeRequired } from '@common/typeutils';
import { booleanish } from '@common/utils';
import { computed } from 'vue';

type T = ExtractValueType<O>;
type R = N extends string ? false : true;

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<WizardQuestionOptionsProps<O, N>>();

const emit = defineEmits<{
  (name: 'update:modelValue', v: MaybeRequired<T | undefined, R>): void;
}>();

type NormalizedOption = {
  label: string;
  value: T;
};

const normalizedOptions = computed(() => {
  const norm: NormalizedOption[] = [];
  if (Array.isArray(props.options)) {
    for (const value of props.options) {
      norm.push({ label: value, value });
    }
  } else {
    for (const [value, label] of Object.entries(props.options)) {
      norm.push({ value: value as T, label });
    }
  }
  return norm;
});

function gridSpan(span: GridSpan) {
  return props.isPopup ? '12' : span;
}
</script>
